import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRenderViewURL, removeFalsyRenderViewOptions } from '../../../../common/renderer/get-render-view-url';
import { rendererDefaultWidth, rendererShareImageWidth } from '../../../../common/renderer/renderer-settings';
import { queryStringValueToRenderOptions } from '../../../../common/renderer/shared';
import { RenderOptions, RenderViewQueryParams } from '../../../../common/renderer/types';
import { useQueryParams } from '../../../common/generic/hooks/url-handling/query-params';

/**
 * If @param initialOptions is given, this hook will use React State to handle changes in options. If not, it uses URL params.
 * Yes, in the latter case the whole state is handled by a single string shown in the address bar. ¯\_(ツ)_/¯
 */
export function useRenderViewOptions(
  initialOptions?: Partial<RenderOptions>,
): [Partial<RenderOptions>, (opts: Partial<RenderOptions>) => void] {
  const [params] = useQueryParams<RenderViewQueryParams>(['data']);
  const optionsInParams: Partial<RenderOptions> = params.data ? queryStringValueToRenderOptions(params.data) : {};
  const navigate = useNavigate();

  const setOptionsToParams = (_newOptions: Partial<RenderOptions>) => {
    // Remove falsy values, we don't need to pollute the URL with those
    const newOptions: Partial<RenderOptions> = removeFalsyRenderViewOptions(_newOptions);
    const url = getRenderViewURL(newOptions);
    navigate(url, { replace: true });
  };

  const [options, setOptions] = initialOptions
    ? // OA: This conditional hook use is fine, as the hook order never changes, i.e. the presence initialOptions param
      // doesn't change within the lifetime of this hook
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useState<Partial<RenderOptions>>(initialOptions)
    : [optionsInParams, setOptionsToParams];

  const width = options.isShareImg ? rendererShareImageWidth : options.width || rendererDefaultWidth;

  return [{ ...options, width }, setOptions];
}
